import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import useModalEnter from '../hooks/useModalEnter'
import { getStatementLiveCasino } from '../services/reportService'

// Account Statement (Game Report) — Result modal behind the "Live Casino" row.
// That row is the net of every third-party casino bet in the range (they get no
// row of their own), so there is no single round to show: pick a provider in
// "Casino Type" and its bets for the same client and range are listed — same
// as the player site's modal. The list (live site's names, then admin Game
// Priority groups, then catalog vendors) and the rows come from
// POST /admin/reports/statementv2/live-casino.

const pad = (n) => String(n).padStart(2, '0')
const fmtDate = (value) => {
  if (!value) return '-'
  const d = new Date(value)
  if (isNaN(d)) return String(value)
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}
const fmtAmt = (n) => (Number(n) || 0).toFixed(2)

function LiveCasinoStatementModal({ clientName, fromDate, toDate, onHide }) {
  const { modalClass, backdropClass, onBackdropClick } = useModalEnter(onHide)
  const [providers, setProviders] = useState([])
  const [casinoType, setCasinoType] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [])

  useEffect(() => {
    let alive = true
    getStatementLiveCasino({ clientName, fromDate, toDate })
      .then((res) => { if (alive) setProviders(res?.success ? res.providers || [] : []) })
      .catch(() => { if (alive) setProviders([]) })
    return () => { alive = false }
  }, [clientName, fromDate, toDate])

  // Picking a provider is what fetches — there is no Submit here
  useEffect(() => {
    if (!casinoType) { setRows([]); return undefined }
    let alive = true
    setLoading(true)
    getStatementLiveCasino({ clientName, fromDate, toDate, provider: casinoType })
      .then((res) => { if (alive) setRows(res?.success ? res.data || [] : []) })
      .catch(() => { if (alive) setRows([]) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [casinoType, clientName, fromDate, toDate])

  return createPortal(
    <div style={{ position: 'absolute', zIndex: 1040 }}>
      <div role="dialog" className={modalClass} aria-modal="true" onClick={onBackdropClick} style={{ display: 'block' }}>
        <div className="modal-dialog modal-xl">
          <span tabIndex="0"></span>
          <div tabIndex="-1" className="modal-content">
            <header className="modal-header">
              <h5 className="modal-title">Result</h5>
              <button type="button" aria-label="Close" className="close" onClick={onHide}>×</button>
            </header>
            <div className="modal-body">
              <div className="form-group d-inline-block">
                <label>Casino Type</label>
                <select className="form-control" value={casinoType} onChange={(e) => setCasinoType(e.target.value)}>
                  <option value="">Select Type</option>
                  {providers.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="table-responsive mt-2">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Game Name</th>
                      <th>Type</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Total</th>
                      <th>Date</th>
                      <th>Round ID</th>
                      <th>Transaction Id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading || rows.length === 0 ? (
                      <tr><td colSpan="7" className="text-center">{loading ? 'Loading…' : 'There are no records to show'}</td></tr>
                    ) : rows.map((r, i) => (
                      <tr key={i}>
                        <td>{r.gameName || '-'}</td>
                        <td>{r.type || '-'}</td>
                        <td className="text-right">{fmtAmt(r.amount)}</td>
                        <td className="text-right">{fmtAmt(r.total)}</td>
                        <td>{fmtDate(r.date)}</td>
                        <td>{r.roundId || '-'}</td>
                        <td>{r.transactionId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <span tabIndex="0"></span>
        </div>
      </div>
      <div className={backdropClass}></div>
    </div>,
    document.body,
  )
}

export default LiveCasinoStatementModal
